"use client";

import * as React from "react";
import { Input } from "@/components/ui/input";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { cn } from "@/lib/utils";

export interface NumericInputProps
  extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type" | "min" | "max"> {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  /** Caps how many digits can be typed (e.g. chip numbers). */
  maxLength?: number;
  /** Unit label rendered inside the field, e.g. "CHF" or "%". */
  suffix?: string;
  /** Adds the destructive border/ring without rendering any error text itself. */
  error?: boolean;
  /** Debounce before `onChange` fires; keystrokes always update the field immediately. */
  debounceMs?: number;
  /** "blur" holds `onChange` back until the field loses focus (or Enter is pressed), e.g. when every
   * change triggers a price calculation. Default: "change" (debounced while typing). */
  commitOn?: "change" | "blur";
  /** Called instead of clamping when a 0 was entered and the field is left (or Enter is pressed), e.g. to switch the
   * coverage the figure belongs to off. The field falls back to what the parent holds. */
  onZero?: () => void;
}

/** A `text` + `inputMode="numeric"` input for whole numbers — avoids the native number spinner
 * and inconsistent mobile keyboards that come with `type="number"`. Keeps its own draft string
 * while typing (so "" and partial input don't get coerced to 0) and debounces `onChange`. On
 * blur, clamps to `min`/`max` if they're set. */
export const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    { value, onChange, min, max, maxLength, suffix, error, disabled, debounceMs = 500, commitOn = "change", onZero, className, onBlur, onKeyDown, ...rest },
    ref
  ) => {
    const [localValue, setLocalValue] = React.useState<string>(value != null ? String(value) : "");
    const debouncedOnChange = useDebouncedCallback(onChange, debounceMs);

    React.useEffect(() => {
      setLocalValue(value != null ? String(value) : "");
    }, [value]);

    const handleChange = (raw: string) => {
      const sanitized = maxLength != null
        ? raw.replace(/\D/g, "").slice(0, maxLength)
        : raw.replace(/\D/g, "");
      setLocalValue(sanitized);
      // A 0 is never handed to the parent while typing when `onZero` handles it — that happens on blur/Enter.
      const pendingZero = onZero != null && sanitized !== "" && Number(sanitized) === 0;
      if (commitOn === "change" && !pendingZero) debouncedOnChange(sanitized === "" ? undefined : Number(sanitized));
    };

    const commit = () => {
      if (localValue === "") {
        if (commitOn === "blur") {
          // Show what the parent holds right now; if it reacts to "cleared" (e.g. falls back to a default)
          // the value effect below updates the field again.
          setLocalValue(value != null ? String(value) : "");
          onChange(undefined);
        }
        return;
      }
      if (onZero && Number(localValue) === 0) {
        setLocalValue(value != null ? String(value) : "");
        onZero();
        return;
      }
      let clamped = Number(localValue);
      if (min != null && clamped < min) clamped = min;
      if (max != null && clamped > max) clamped = max;
      if (String(clamped) !== localValue) setLocalValue(String(clamped));
      if (commitOn === "blur" ? clamped !== value : String(clamped) !== localValue) onChange(clamped);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      commit();
      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && (commitOn === "blur" || (onZero && localValue !== "" && Number(localValue) === 0))) commit();
      onKeyDown?.(e);
    };

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn(suffix && "pr-12", error && "border-destructive focus-visible:ring-destructive", className)}
          {...rest}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    );
  }
);
NumericInput.displayName = "NumericInput";
